// Content for the Communications page "Résultats en production" (Brossard) section.
export type Bi = { fr: string; en: string }

// Brossard logo is cropped from the supplied reference visual (no separate logo file was provided).
export const PROOF_REFERENCE = 'https://cdn.magicpatterns.com/uploads/gBYab1NW1D2aoVx8cNxVyd/Image_ChatGPT_27_sept._2026,_20_17_01.png'
export const BROSSARD_PANORAMA = 'https://cdn.magicpatterns.com/patterns/generated-images/bdda70cb-8769-4c08-ac45-22321186e1d1.jpg'

export const proofIntro = {
  eyebrow: { fr: 'Résultats en production', en: 'Results in production' },
  line1: { fr: 'Quand chaque minute compte,', en: 'When every minute counts,' },
  line2: { fr: 'la portée n’est pas théorique.', en: 'reach isn’t theoretical.' },
  body: {
    fr: [
      'À Brossard, BCITI | ATNA fait partie de l’infrastructure de communication utilisée pour informer',
      'une communauté de près de 100 000 citoyens — du quotidien aux communications d’urgence.',
    ],
    en: [
      'In Brossard, BCITI | ATNA is part of the communications infrastructure used to inform',
      'a community of nearly 100,000 residents — from everyday updates to emergency communications.',
    ],
  },
  client: { fr: 'Client depuis 2017', en: 'Client since 2017' },
}

export type CapabilityKey = 'map' | 'notices' | 'requests' | 'collect' | 'analytics'

// Exactly the five capabilities Brossard uses — do not add others.
export const brossardCapabilities: { key: CapabilityKey; title: Bi; body: Bi }[] = [
  { key: 'map', title: { fr: 'Carte numérique', en: 'Digital map' }, body: { fr: 'Information géographique et services municipaux.', en: 'Geographic information and municipal services.' } },
  { key: 'notices', title: { fr: 'Avis et notifications', en: 'Notices and notifications' }, body: { fr: 'Communications ciblées du quotidien et d’urgence.', en: 'Targeted everyday and emergency communications.' } },
  { key: 'requests', title: { fr: 'Requêtes ATNA', en: 'ATNA Requests' }, body: { fr: 'Signalements citoyens et suivi des demandes.', en: 'Resident reports and request tracking.' } },
  { key: 'collect', title: { fr: 'Calendrier des collectes', en: 'Waste collection calendar' }, body: { fr: 'Information personnalisée et rappels.', en: 'Personalized information and reminders.' } },
  { key: 'analytics', title: { fr: 'Analytique', en: 'Analytics' }, body: { fr: 'Suivi de la portée et des résultats, et recommandations.', en: 'Tracking reach, results and recommendations.' } },
]

export const proofMetrics: { key: 'registered' | 'speed' | 'since'; value: Bi; label: Bi; body: Bi; info?: boolean }[] = [
  {
    key: 'registered',
    value: { fr: 'Plus de 88 000', en: '88,000+' },
    label: { fr: 'Citoyens inscrits', en: 'Registered residents' },
    body: { fr: 'sur une population d’environ 97 000 citoyens', en: 'out of a population of about 97,000' },
    info: true,
  },
  {
    key: 'speed',
    value: { fr: '< 30 min', en: '< 30 min' },
    label: { fr: 'Pour joindre toute la population par appel vocal en situation d’urgence', en: 'To reach the population by voice in an emergency' },
    body: { fr: 'Capacité éprouvée grâce aux notifications multicanales (voix, SMS, push, courriel).', en: 'Proven capability with multichannel notifications (voice, SMS, push, email).' },
  },
  {
    key: 'since',
    value: { fr: 'Depuis 2017', en: 'Since 2017' },
    label: { fr: 'En production', en: 'In production' },
    body: { fr: 'Une relation de confiance à long terme.', en: 'A long-term relationship built on trust.' },
  },
]

export const previewCopy = {
  notifApp: 'Brossard',
  notifNow: { fr: 'maintenant', en: 'now' },
  notifTitle: { fr: 'Avis important', en: 'Important notice' },
  notifBody: { fr: 'Bris d’aqueduc sur Grande-Allée jusqu’à 16 h. Suivez les mises à jour.', en: 'Water main break on Grande-Allée until 4 PM. Follow the updates.' },
  newRequest: { fr: 'Nouvelle demande', en: 'New request' },
  requestTitle: { fr: 'Nid-de-poule', en: 'Pothole' },
  requestId: '#REQ-12345',
  requestStatus: { fr: 'En cours', en: 'In progress' },
  collectTitle: { fr: 'Collecte des matières résiduelles', en: 'Waste collection' },
  collectDate: { fr: 'Jeudi 12 septembre', en: 'Thursday, September 12' },
  reach: { fr: 'Portée', en: 'Reach' },
  reachValue: { fr: '91 %', en: '91%' },
  reachTrend: { fr: '+6 %', en: '+6%' },
}

export const phoneCopy = {
  search: { fr: 'Que cherchez-vous ?', en: 'What are you looking for?' },
  shortcuts: [
    { key: 'map', label: { fr: 'Carte', en: 'Map' } },
    { key: 'notices', label: { fr: 'Avis', en: 'Notices' } },
    { key: 'requests', label: { fr: 'Requêtes', en: 'Requests' } },
    { key: 'collect', label: { fr: 'Collectes', en: 'Waste collection' } },
    { key: 'calendar', label: { fr: 'Calendrier', en: 'Calendar' } },
  ],
}

export const dashCopy = {
  nav: [
    { key: 'home', label: { fr: 'Tableau de bord', en: 'Dashboard' } },
    { key: 'map', label: { fr: 'Carte', en: 'Map' } },
    { key: 'comms', label: { fr: 'Communications', en: 'Communications' } },
    { key: 'requests', label: { fr: 'Requêtes', en: 'Requests' } },
    { key: 'collect', label: { fr: 'Collectes', en: 'Waste collection' } },
    { key: 'analytics', label: { fr: 'Analytique', en: 'Analytics' } },
  ],
  overview: { fr: 'Aperçu général', en: 'Overview' },
  period: { fr: '30 derniers jours', en: 'Last 30 days' },
  kpis: [
    { label: { fr: 'Avis envoyés', en: 'Notices sent' }, value: { fr: '125 842', en: '125,842' }, trend: '+18 %' },
    { label: { fr: 'Citoyens joints', en: 'Residents reached' }, value: { fr: '91 %', en: '91%' } },
    { label: { fr: 'Taux d’ouverture', en: 'Open rate' }, value: { fr: '73 %', en: '73%' } },
  ],
  channelsTitle: { fr: 'Performance par canal', en: 'Performance by channel' },
  channels: [
    { key: 'push', label: { fr: 'Push mobile', en: 'Mobile push' }, sent: { fr: '85 420', en: '85,420' }, open: 88 },
    { key: 'sms', label: { fr: 'SMS', en: 'SMS' }, sent: { fr: '42 100', en: '42,100' }, open: 92 },
    { key: 'email', label: { fr: 'Courriel', en: 'Email' }, sent: { fr: '112 500', en: '112,500' }, open: 45 },
    { key: 'voice', label: { fr: 'Voix', en: 'Voice' }, sent: { fr: '8 400', en: '8,400' }, open: 75 },
  ],
  coverageTitle: { fr: 'Couverture géographique', en: 'Geographic coverage' },
  district: {
    name: { fr: 'Secteur A', en: 'Sector A' },
    lines: [
      { fr: '12 450 citoyens', en: '12,450 residents' },
      { fr: '78 % d’ouverture', en: '78% open rate' },
      { fr: '45 avis envoyés', en: '45 notices sent' },
    ],
  },
}

export const outcomes: { key: 'community' | 'timing' | 'engaged' | 'decisions'; title: Bi; body: Bi }[] = [
  { key: 'community', title: { fr: 'Une communauté connectée', en: 'A connected community' }, body: { fr: 'La grande majorité des citoyens sont inscrits et informés.', en: 'A large majority of residents registered and informed.' } },
  { key: 'timing', title: { fr: 'Une information au bon moment', en: 'Information at the right time' }, body: { fr: 'Du quotidien aux urgences, des communications ciblées et efficaces.', en: 'From everyday updates to emergencies, targeted and effective communications.' } },
  { key: 'engaged', title: { fr: 'Des citoyens plus engagés', en: 'More engaged residents' }, body: { fr: 'Requêtes, collectes et services plus accessibles.', en: 'Requests, waste collection and services that are easier to access.' } },
  { key: 'decisions', title: { fr: 'Des décisions éclairées', en: 'Informed decisions' }, body: { fr: 'Une analytique qui mesure la portée et favorise l’amélioration continue.', en: 'Analytics that measure reach and support continuous improvement.' } },
]
